import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from '@mui/material'
import {
  DeviceHub as DeviceIcon,
  Security as SecurityIcon,
  ExitToApp as RevokeIcon,
  PowerSettingsNew as RevokeAllIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { useAppSelector } from '../store/hooks'
import { sessionApi, SessionInfo } from '../services/api/sessionApi'

export const SessionManagement: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth)
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null)
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revokingSession, setRevokingSession] = useState<string | null>(null)
  const [revokingAllSessions, setRevokingAllSessions] = useState(false)

  const loadSessions = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await sessionApi.getUserSessions()
      setSessions(response.sessions)
    } catch (err) {
      setError('Failed to load sessions')
      console.error('Failed to load sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSessions()
  }, [])

  // Get current session ID (this would come from auth context or local storage)
  const getCurrentSessionId = () => {
    // For now, we'll use the most recent session as current
    if (sessions.length === 0) return null
    return sessions.reduce((mostRecent, session) => 
      new Date(session.lastActivity) > new Date(mostRecent.lastActivity) ? session : mostRecent
    ).sessionId
  }

  const currentSessionId = getCurrentSessionId()

  const handleRevokeSession = async (sessionId: string) => {
    try {
      setRevokingSession(sessionId)
      const success = await sessionApi.terminateSession(sessionId)
      if (success) {
        setSnackbar({ message: 'Session revoked successfully', severity: 'success' })
        await loadSessions() // Refresh the sessions list
      } else {
        setSnackbar({ message: 'Failed to revoke session', severity: 'error' })
      }
    } catch (_error) {
      setSnackbar({ message: 'Failed to revoke session', severity: 'error' })
    } finally {
      setRevokingSession(null)
    }
  }

  const handleRevokeAllOtherSessions = async () => {
    try {
      setRevokingAllSessions(true)
      const success = await sessionApi.terminateOtherSessions()
      if (success) {
        setSnackbar({ message: 'All other sessions revoked successfully', severity: 'success' })
        await loadSessions() // Refresh the sessions list
      } else {
        setSnackbar({ message: 'Failed to revoke sessions', severity: 'error' })
      }
    } catch (_error) {
      setSnackbar({ message: 'Failed to revoke sessions', severity: 'error' })
    } finally {
      setRevokingAllSessions(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getSessionDisplayName = (session: SessionInfo) => {
    const userAgent = session.userAgent || 'Unknown Device'
    // Extract browser and OS from user agent
    let browser = 'Unknown'
    let os = 'Unknown'
    
    if (userAgent.includes('Chrome')) browser = 'Chrome'
    else if (userAgent.includes('Firefox')) browser = 'Firefox'
    else if (userAgent.includes('Safari')) browser = 'Safari'
    else if (userAgent.includes('Edge')) browser = 'Edge'
    
    if (userAgent.includes('Windows')) os = 'Windows'
    else if (userAgent.includes('Mac')) os = 'macOS'
    else if (userAgent.includes('Linux')) os = 'Linux'
    else if (userAgent.includes('Android')) os = 'Android'
    else if (userAgent.includes('iOS')) os = 'iOS'
    
    return `${browser} on ${os}`
  }

  const isCurrentSession = (sessionId: string) => {
    return sessionId === currentSessionId
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error}. Please try again later.
        </Alert>
      </Box>
    )
  }

  return (
    <Box p={3} maxWidth={1200} mx="auto">
      {/* Header */}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" component="h1">
          Session Management
        </Typography>
        <IconButton onClick={() => loadSessions()} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* User Info */}
      {user && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Account: {user.email}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage your active sessions across all devices
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Sessions List */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Active Sessions ({sessions.length})
            </Typography>
            {sessions.length > 1 && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RevokeAllIcon />}
                onClick={handleRevokeAllOtherSessions}
                disabled={revokingAllSessions}
              >
                {revokingAllSessions ? 'Revoking...' : 'Revoke All Other Sessions'}
              </Button>
            )}
          </Box>

          {sessions.length === 0 ? (
            <Box textAlign="center" py={4}>
              <DeviceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No active sessions found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You are not currently logged in on any device.
              </Typography>
            </Box>
          ) : (
            <List>
              {sessions.map((session, index) => (
                <React.Fragment key={session.sessionId}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {getSessionDisplayName(session)}
                          </Typography>
                          {isCurrentSession(session.sessionId) && (
                            <Chip
                              label="Current session"
                              color="primary"
                              size="small"
                            />
                          )}
                          {session.isActive ? (
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Inactive"
                              color="default"
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>IP Address:</strong> {session.ip}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Session Start:</strong> {formatDate(session.sessionStart)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Last Active:</strong> {formatDate(session.lastActivity)}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="caption" color="textSecondary">
                              {session.userAgent}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {!isCurrentSession(session.sessionId) && (
                        <Tooltip title="Revoke this session">
                          <IconButton
                            edge="end"
                            onClick={() => handleRevokeSession(session.sessionId)}
                            disabled={revokingSession === session.sessionId}
                            color="error"
                            data-testid="RevokeIcon"
                          >
                            {revokingSession === session.sessionId ? (
                              <CircularProgress size={20} />
                            ) : (
                              <RevokeIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < sessions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Security Information */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">Security Tips</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                • If you don&apos;t recognize a device, revoke its session immediately
              </Typography>
              <Typography variant="body2" paragraph>
                • Regularly review your active sessions for security
              </Typography>
              <Typography variant="body2">
                • Always log out from shared or public devices
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DeviceIcon color="primary" />
                <Typography variant="h6">Device Information</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Each session includes device fingerprinting information:
              </Typography>
              <Typography variant="body2" paragraph>
                • IP Address for location tracking
              </Typography>
              <Typography variant="body2" paragraph>
                • Browser and operating system details
              </Typography>
              <Typography variant="body2">
                • Unique session identifier for accurate tracking
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      {snackbar && (
        <Snackbar
          open={!!snackbar}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  )
}
